let tokenGetter: (() => Promise<string | null>) | null = null;
let lastAuthError: string | null = null;
const AUTH_TOKEN_TIMEOUT_MS = 12000;

async function withAuthTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

function getCachedAuth0Token() {
  if (typeof window === 'undefined') return null;
  try {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const auth0Keys = Object.keys(window.localStorage).filter((key) => key.startsWith('@@auth0spajs@@'));
    for (const key of auth0Keys) {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as {
        body?: { access_token?: string };
        expiresAt?: number;
      };
      const token = parsed.body?.access_token;
      if (!token) continue;
      if (typeof parsed.expiresAt === 'number' && parsed.expiresAt <= nowSeconds + 30) {
        continue;
      }
      return token;
    }
  } catch (error) {
    console.warn('[auth] unable to inspect Auth0 local storage cache', error);
  }
  return null;
}

export function registerAuthTokenGetter(getter: (() => Promise<string | null>) | null) {
  tokenGetter = getter;
  lastAuthError = null;
}

export async function getAuthToken() {
  if (!tokenGetter) {
    const fallbackToken = getCachedAuth0Token();
    if (fallbackToken) {
      lastAuthError = null;
      return fallbackToken;
    }
    return null;
  }
  try {
    const token = await withAuthTimeout(
      tokenGetter(),
      AUTH_TOKEN_TIMEOUT_MS,
      `Auth token request timed out after ${AUTH_TOKEN_TIMEOUT_MS / 1000}s.`,
    );
    if (token) {
      lastAuthError = null;
      return token;
    }
    const fallbackToken = getCachedAuth0Token();
    if (fallbackToken) {
      lastAuthError = null;
      return fallbackToken;
    }
    return null;
  } catch (error) {
    lastAuthError =
      error instanceof Error ? error.message : typeof error === 'string' ? error : 'unknown auth error retrieving token';
    const fallbackToken = getCachedAuth0Token();
    if (fallbackToken) {
      lastAuthError = null;
      return fallbackToken;
    }
    return null;
  }
}

export function getLastAuthError() {
  return lastAuthError;
}
